// 视频回放时间轴 — 24h / 1h / 10min 三档缩放 + 拖拽平移 + pinch + 点击/单指选时
// 数据：CloudSegment[] 来自 /api/cloud-history（已接通 m3u8api.ulucu.com/video/history）
// onSeek(utc, seg) — 用户点击/拖到某个时刻；seg 为 null 表示该时刻无录像
import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import type { CloudSegment } from '../api/cloud-history.js';

export type ZoomLevel = '24h' | '1h' | '10min';

const ZOOM_SECONDS: Record<ZoomLevel, number> = {
	'24h': 86400,
	'1h': 3600,
	'10min': 600,
};

const PRIMARY = '#FF7043';

export interface VideoTimelineProps {
	selectedDate: string; // 'YYYY-MM-DD' | 'live' | 'all'
	segments: CloudSegment[] | null;
	loading: boolean;
	error: string | null;
	emptyText: string;
	loadingText?: string;
	segmentCountText?: (count: number) => string;
	onSeek?: (utc: number, segment: CloudSegment | null) => void;
}

export default function VideoTimeline({
	selectedDate,
	segments,
	loading,
	error,
	emptyText,
	loadingText = 'Loading...',
	segmentCountText = (count) => `${count} segments`,
	onSeek,
}: VideoTimelineProps) {
	const isLive = selectedDate === 'live';
	const isAll = selectedDate === 'all';

	const dayStart = useMemo(() => {
		if (isLive || isAll) return dayjs().startOf('day').unix();
		return dayjs(selectedDate).startOf('day').unix();
	}, [selectedDate, isLive, isAll]);

	const [zoom, setZoom] = useState<ZoomLevel>('24h');
	const [viewportStart, setViewportStart] = useState<number>(dayStart);
	const [scrubTime, setScrubTime] = useState<number | null>(null);

	// 切日期 / live → 重置视窗 + 缩放档
	useEffect(() => {
		setZoom('24h');
		setViewportStart(dayStart);
		setScrubTime(null);
	}, [dayStart]);

	const viewportSeconds = ZOOM_SECONDS[zoom];
	const viewportEnd = viewportStart + viewportSeconds;

	const trackRef = useRef<HTMLDivElement>(null);
	const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
	const dragRef = useRef<{ startX: number; startVp: number; moved: boolean } | null>(null);
	const pinchRef = useRef<{ dist: number; zoom: ZoomLevel; centerUtc: number } | null>(null);

	const clampVp = (vp: number, z: ZoomLevel): number => {
		const sec = ZOOM_SECONDS[z];
		const dayEnd = dayStart + 86400;
		return Math.max(dayStart, Math.min(dayEnd - sec, vp));
	};

	const utcAtClientX = (clientX: number, fallbackVp = viewportStart, fallbackSec = viewportSeconds): number => {
		const rect = trackRef.current?.getBoundingClientRect();
		if (!rect) return fallbackVp;
		const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
		return fallbackVp + ratio * fallbackSec;
	};

	const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		if (isLive || isAll) return;
		try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
		pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		if (pointersRef.current.size === 1) {
			dragRef.current = { startX: e.clientX, startVp: viewportStart, moved: false };
		} else if (pointersRef.current.size === 2) {
			const pts = [...pointersRef.current.values()];
			const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
			const centerX = (pts[0].x + pts[1].x) / 2;
			pinchRef.current = { dist, zoom, centerUtc: utcAtClientX(centerX) };
			dragRef.current = null;
		}
	};

	const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
		if (!pointersRef.current.has(e.pointerId)) return;
		pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

		// pinch 处理（双指）
		if (pointersRef.current.size === 2 && pinchRef.current) {
			const pts = [...pointersRef.current.values()];
			const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
			const ratio = dist / pinchRef.current.dist;
			let nextZoom: ZoomLevel | null = null;
			if (ratio > 1.4) nextZoom = pinchUp(pinchRef.current.zoom);
			else if (ratio < 0.7) nextZoom = pinchDown(pinchRef.current.zoom);
			if (nextZoom && nextZoom !== zoom) {
				const newSec = ZOOM_SECONDS[nextZoom];
				const rect = trackRef.current?.getBoundingClientRect();
				const centerX = (pts[0].x + pts[1].x) / 2;
				const centerRatio = rect ? (centerX - rect.left) / rect.width : 0.5;
				const newVp = clampVp(pinchRef.current.centerUtc - centerRatio * newSec, nextZoom);
				setZoom(nextZoom);
				setViewportStart(newVp);
				// pinch 后重置基线，避免连续触发
				pinchRef.current = { dist, zoom: nextZoom, centerUtc: pinchRef.current.centerUtc };
			}
			return;
		}

		// 单指拖动平移
		if (pointersRef.current.size === 1 && dragRef.current) {
			const dx = e.clientX - dragRef.current.startX;
			if (Math.abs(dx) > 4) dragRef.current.moved = true;
			const rect = trackRef.current?.getBoundingClientRect();
			if (!rect) return;
			const dSec = -(dx / rect.width) * viewportSeconds;
			setViewportStart(clampVp(dragRef.current.startVp + dSec, zoom));
		}
	};

	const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
		const moved = dragRef.current?.moved ?? false;
		const wasSinglePointer = pointersRef.current.size === 1;
		pointersRef.current.delete(e.pointerId);
		if (pointersRef.current.size === 0) {
			pinchRef.current = null;
			// 单击未拖动 → 选时
			if (wasSinglePointer && !moved && !isLive && !isAll) {
				const utc = utcAtClientX(e.clientX);
				setScrubTime(utc);
				const seg = (segments ?? []).find((s) => utc >= s.start_utc && utc <= s.end_utc) ?? null;
				onSeek?.(utc, seg);
			}
			dragRef.current = null;
		}
	};

	const onDoubleClick = () => {
		if (isLive || isAll) return;
		const nextZoom = pinchUp(zoom);
		if (nextZoom === zoom) return;
		const anchor = scrubTime ?? viewportStart + viewportSeconds / 2;
		const newSec = ZOOM_SECONDS[nextZoom];
		setZoom(nextZoom);
		setViewportStart(clampVp(anchor - newSec / 2, nextZoom));
	};

	// live 模式：每秒更新游标
	const [now, setNow] = useState(() => dayjs().unix());
	useEffect(() => {
		if (!isLive) return;
		const id = setInterval(() => setNow(dayjs().unix()), 1000);
		return () => clearInterval(id);
	}, [isLive]);

	const cursorUtc = isLive ? now : scrubTime;
	const cursorRatio = cursorUtc != null ? (cursorUtc - viewportStart) / viewportSeconds : null;

	const ticks = useMemo(() => buildTicks(zoom, viewportStart), [zoom, viewportStart]);

	const renderedSegs = (segments ?? [])
		.filter((s) => s.end_utc > viewportStart && s.start_utc < viewportEnd)
		.map((s) => {
			const left = Math.max(0, (s.start_utc - viewportStart) / viewportSeconds);
			const right = Math.min(1, (s.end_utc - viewportStart) / viewportSeconds);
			return { left: left * 100, width: Math.max(0.3, (right - left) * 100), raw: s };
		});

	const headerLabel = scrubTime
		? dayjs.unix(scrubTime).format('HH:mm:ss')
		: isLive
			? dayjs.unix(now).format('HH:mm:ss')
			: `${zoom} ${dayjs.unix(viewportStart).format('HH:mm')}-${dayjs.unix(viewportEnd).format('HH:mm')}`;

	const showZoomChips = !isLive && !isAll;

	const setZoomTo = (z: ZoomLevel) => {
		if (z === zoom) return;
		const anchor = scrubTime ?? viewportStart + viewportSeconds / 2;
		const newSec = ZOOM_SECONDS[z];
		setZoom(z);
		setViewportStart(clampVp(anchor - newSec / 2, z));
	};

	return (
		<div style={{ background: '#fff', padding: '0 12px 12px', userSelect: 'none' }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
				<span style={{ color: PRIMARY, fontFamily: 'monospace', fontSize: 13 }}>{headerLabel}</span>
				{showZoomChips && (
					<div style={{ display: 'flex', gap: 4 }}>
						{(['24h', '1h', '10min'] as ZoomLevel[]).map((z) => (
							<span
								key={z}
								onClick={() => setZoomTo(z)}
								style={{
									padding: '2px 8px',
									borderRadius: 4,
									fontSize: 11,
									border: `1px solid ${zoom === z ? PRIMARY : '#d9d9d9'}`,
									color: zoom === z ? PRIMARY : '#666',
									cursor: 'pointer',
								}}
							>
								{z}
							</span>
						))}
					</div>
				)}
			</div>

			<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666', padding: '2px 0' }}>
				{ticks.map((tk, i) => (
					<span
						key={i}
						style={{
							flex: 1,
							textAlign: i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'center',
						}}
					>
						{tk.label}
					</span>
				))}
			</div>

			<div
				ref={trackRef}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerUp}
				onPointerCancel={onPointerUp}
				onDoubleClick={onDoubleClick}
				style={{
					position: 'relative',
					height: 36,
					borderRadius: 4,
					background: '#f5f5f5',
					overflow: 'hidden',
					touchAction: 'none',
					cursor: isLive || isAll ? 'default' : 'grab',
				}}
			>
				{ticks.map((tk, i) => (
					<div
						key={i}
						style={{
							position: 'absolute',
							insetInlineStart: `${tk.ratio * 100}%`,
							top: 0,
							bottom: 0,
							width: 1,
							background: '#e0e0e0',
						}}
					/>
				))}

				{renderedSegs.map((seg, i) => (
					<div
						key={i}
						style={{
							position: 'absolute',
							insetInlineStart: `${seg.left}%`,
							width: `${seg.width}%`,
							top: 8,
							bottom: 8,
							minWidth: 2,
							background: 'rgba(255, 112, 67, 0.6)',
							borderRadius: 2,
						}}
					/>
				))}

				{cursorRatio != null && cursorRatio >= 0 && cursorRatio <= 1 && (
					<div
						style={{
							position: 'absolute',
							insetInlineStart: `${cursorRatio * 100}%`,
							top: 0,
							bottom: 0,
							width: 2,
							background: PRIMARY,
							boxShadow: '0 0 4px rgba(255,112,67,0.6)',
						}}
					/>
				)}
			</div>

			{!isLive && !isAll && (
				<div style={{ marginTop: 6, fontSize: 11, color: '#999', textAlign: 'center' }}>
					{loading
						? loadingText
						: error
							? error
							: segments && segments.length === 0
								? emptyText
								: segments && segmentCountText(segments.length)}
				</div>
			)}
		</div>
	);
}

function pinchUp(z: ZoomLevel): ZoomLevel {
	if (z === '24h') return '1h';
	if (z === '1h') return '10min';
	return '10min';
}

function pinchDown(z: ZoomLevel): ZoomLevel {
	if (z === '10min') return '1h';
	if (z === '1h') return '24h';
	return '24h';
}

function buildTicks(zoom: ZoomLevel, viewportStart: number): { ratio: number; label: string }[] {
	const totalSec = ZOOM_SECONDS[zoom];
	const SEGMENTS = 6; // 7 个刻度，6 段
	const fmt = zoom === '10min' ? 'mm:ss' : 'HH:mm';
	const out: { ratio: number; label: string }[] = [];
	for (let i = 0; i <= SEGMENTS; i++) {
		const utc = viewportStart + (i / SEGMENTS) * totalSec;
		out.push({ ratio: i / SEGMENTS, label: dayjs.unix(utc).format(fmt) });
	}
	return out;
}
